import { LBS_SUPPORT_SIGNATURE } from "@/modules/tickets/ticketReplyTemplates";
import { TICKET_REPLY_QUOTE_SELECTOR } from "@/modules/tickets/ticketReplyQuotedThread";
import {
  buildDefaultLbsReplySignatureHtml,
  buildWorkspaceFallbackSignatureHtml,
  isHomeReplySignatureWorkspace,
  parseLbsReplySignatureConfigFromHtml,
} from "@/modules/tickets/lbsDefaultReplySignature";

/**
 * Email HTML is parsed in a detached document: the live page would fetch its
 * images while parsing, including tracking pixels in quoted mail (which then
 * report the email as opened and spam the console when an ad blocker stops
 * them).
 */
let inertDoc: Document | null = null;
const inertDocument = () =>
  (inertDoc ??= document.implementation.createHTMLDocument(""));

export const TICKET_REPLY_SIGNATURE_SELECTOR =
  '[data-ticket-reply-signature="true"]';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const BLOCK_BREAK_TAG =
  /<\/?(?:p|div|li|tr|h[1-6]|blockquote|section|article|header|footer|pre)(?:\s[^>]*)?>/gi;

const collapsePlainTextWhitespace = (text: string) =>
  text
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Strip cid: (and other non-fetchable) image sources before assigning
 * innerHTML — browsers eagerly request img src and log ERR_UNKNOWN_URL_SCHEME.
 */
const stripUnresolvedInlineImageTags = (html: string) =>
  html.replace(/<img\b[^>]*\bsrc\s*=\s*(["']?)cid:[^"'>\s]*\1[^>]*>/gi, "");

/** Parts of an email that are never shown: head, styles, scripts, comments. */
export const stripInvisibleEmailHtml = (html: string) =>
  html
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<head\b[\s\S]*?<\/head\s*>/gi, " ")
    .replace(
      /<(style|script|title|noscript|template)\b[\s\S]*?<\/\1\s*>/gi,
      " ",
    )
    .replace(/<(style|script)\b[\s\S]*$/i, " ");

export const htmlToPlainText = (html: string) => {
  if (!html.trim()) return "";

  // A detached element's text includes <style> rules: drop them first.
  const normalizedHtml = stripUnresolvedInlineImageTags(
    stripInvisibleEmailHtml(html)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(BLOCK_BREAK_TAG, "\n"),
  );

  if (typeof document === "undefined") {
    return collapsePlainTextWhitespace(normalizedHtml.replace(/<[^>]+>/g, ""));
  }

  const container = inertDocument().createElement("div");
  container.innerHTML = normalizedHtml;
  const text = container.innerText || container.textContent || "";
  return collapsePlainTextWhitespace(text);
};

export const plainTextToEditorHtml = (text: string) => {
  const normalized = text.replace(/\r\n/g, "\n").trim();
  if (!normalized) return "<p><br></p>";

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => {
      const lines = paragraph.split("\n").map(escapeHtml).join("<br>");
      return `<p>${lines || "<br>"}</p>`;
    })
    .join("");
};

const SIGNATURE_WRAPPER_STYLE =
  "margin-top:12px;color:#374151;font-size:13px;line-height:1.5;";

export const buildReplySignatureEditorHtml = (
  signatureHtml?: string | null,
) => {
  if (signatureHtml?.trim()) {
    const trimmed = signatureHtml.trim();
    // Rebuild LBS signatures so the composer matches outbound (PNG icons).
    if (trimmed.includes("data-lbs-signature")) {
      const parsed = parseLbsReplySignatureConfigFromHtml(trimmed);
      return `<div data-ticket-reply-signature="true" style="${SIGNATURE_WRAPPER_STYLE}">${buildDefaultLbsReplySignatureHtml(parsed)}</div>`;
    }
    if (trimmed.includes("<")) {
      return `<div data-ticket-reply-signature="true" style="${SIGNATURE_WRAPPER_STYLE}">${trimmed}</div>`;
    }
    const lines = trimmed
      .split("\n")
      .map((line) => `<p style="margin:0;">${escapeHtml(line)}</p>`)
      .join("");
    return `<div data-ticket-reply-signature="true" style="${SIGNATURE_WRAPPER_STYLE}">${lines}</div>`;
  }

  // Same default the email gets (ticketReplySignature.buildSignatureHtml).
  const fallback = isHomeReplySignatureWorkspace()
    ? buildDefaultLbsReplySignatureHtml()
    : buildWorkspaceFallbackSignatureHtml();
  return `<div data-ticket-reply-signature="true" style="${SIGNATURE_WRAPPER_STYLE}">${fallback}</div>`;
};

export const createDefaultReplyHtml = (
  signatureHtml?: string | null,
  quotedHtml?: string | null,
) =>
  `<p><br></p>${buildReplySignatureEditorHtml(signatureHtml)}${quotedHtml ?? ""}`;

export const includesReplySignatureHtml = (html: string) =>
  html.includes('data-ticket-reply-signature="true"') ||
  html.includes(LBS_SUPPORT_SIGNATURE);

export const stripReplySignatureHtml = (html: string) => {
  if (typeof document === "undefined") {
    return html;
  }

  const container = inertDocument().createElement("div");
  container.innerHTML = html;
  container
    .querySelectorAll(TICKET_REPLY_SIGNATURE_SELECTOR)
    .forEach((node) => node.remove());

  return container.innerHTML.trim();
};

export const stripQuotedReplyHtml = (html: string) => {
  if (typeof document === "undefined") {
    return html;
  }

  const container = inertDocument().createElement("div");
  container.innerHTML = html;
  container
    .querySelectorAll(TICKET_REPLY_QUOTE_SELECTOR)
    .forEach((node) => node.remove());

  return container.innerHTML.trim();
};

export const extractReplyComposerParts = (html: string) => {
  if (typeof document === "undefined") {
    return {
      userNoteHtml: stripQuotedReplyHtml(stripReplySignatureHtml(html)),
      signatureHtml: "",
      quotedReplyHtml: "",
    };
  }

  const container = inertDocument().createElement("div");
  container.innerHTML = html;
  const signature = container.querySelector(TICKET_REPLY_SIGNATURE_SELECTOR);
  const quote = container.querySelector(TICKET_REPLY_QUOTE_SELECTOR);
  const signatureHtml = signature?.outerHTML ?? "";
  const quotedReplyHtml = quote?.outerHTML ?? "";

  quote?.remove();
  signature?.remove();

  return {
    userNoteHtml: container.innerHTML.trim(),
    signatureHtml,
    quotedReplyHtml,
  };
};

export const assembleReplyComposerHtml = ({
  userNoteHtml,
  signatureHtml,
  quotedReplyHtml,
}: {
  userNoteHtml: string;
  signatureHtml?: string;
  quotedReplyHtml?: string;
}) => {
  const user = userNoteHtml.trim() || "<p><br></p>";
  return `${user}${signatureHtml ?? ""}${quotedReplyHtml ?? ""}`;
};

export const shouldShowComposerPlaceholder = (html: string) => {
  const { userNoteHtml } = extractReplyComposerParts(html);
  return !htmlToPlainText(userNoteHtml).trim();
};

export const stripReplyComposerMetaHtml = (html: string) =>
  extractReplyComposerParts(html).userNoteHtml;

export const hasReplyContentHtml = (html: string) => {
  const plain = htmlToPlainText(stripReplyComposerMetaHtml(html));
  return Boolean(plain.trim());
};

const isEmptyEditorBlockHtml = (html: string) => !htmlToPlainText(html).trim();

const removeEmptyLeadingBlocks = (
  container: HTMLElement,
  stopBefore: Element | null,
) => {
  let node = container.firstChild;
  while (node && node !== stopBefore) {
    if (node.nodeType !== Node.ELEMENT_NODE) break;
    const element = node as HTMLElement;
    if (element.matches(TICKET_REPLY_SIGNATURE_SELECTOR)) break;
    if (!isEmptyEditorBlockHtml(element.outerHTML)) break;
    const next = node.nextSibling;
    element.remove();
    node = next;
  }
};

export const insertAboveSignatureHtml = (html: string, text: string) => {
  const insertion = plainTextToEditorHtml(text);
  if (!includesReplySignatureHtml(html)) {
    const trimmed = html.trim();
    if (!trimmed) return `${insertion}${buildReplySignatureEditorHtml()}`;
    return `${trimmed}${insertion}${buildReplySignatureEditorHtml()}`;
  }

  if (typeof document === "undefined") {
    return `${insertion}${html}`;
  }

  const container = inertDocument().createElement("div");
  container.innerHTML = html;
  const signature = container.querySelector(TICKET_REPLY_SIGNATURE_SELECTOR);
  if (!signature) {
    return `${insertion}${html}`;
  }

  removeEmptyLeadingBlocks(container, signature);

  const wrapper = inertDocument().createElement("div");
  wrapper.innerHTML = insertion;
  while (wrapper.firstChild) {
    container.insertBefore(wrapper.firstChild, signature);
  }

  removeEmptyLeadingBlocks(container, signature);

  return container.innerHTML;
};

export const insertBelowSignatureHtml = (
  html: string,
  insertionHtml: string,
) => {
  const insertion = insertionHtml.trim();
  if (!insertion) return html;

  if (typeof document === "undefined") {
    return `${html}${insertion}`;
  }

  const container = inertDocument().createElement("div");
  container.innerHTML = html;
  container
    .querySelectorAll(TICKET_REPLY_QUOTE_SELECTOR)
    .forEach((node) => node.remove());

  const signature = container.querySelector(TICKET_REPLY_SIGNATURE_SELECTOR);
  if (!signature) {
    return `${html}${insertion}`;
  }

  const wrapper = inertDocument().createElement("div");
  wrapper.innerHTML = insertion;
  let anchor: ChildNode | null = signature.nextSibling;
  while (wrapper.firstChild) {
    const child = wrapper.firstChild;
    container.insertBefore(child, anchor);
    anchor = child.nextSibling;
  }

  return container.innerHTML;
};

export const focusRichEditor = (editor: HTMLElement | null) => {
  if (!editor) return;
  editor.focus();
};

export const execRichEditorCommand = (
  editor: HTMLElement | null,
  command: string,
  value?: string,
) => {
  focusRichEditor(editor);
  document.execCommand(command, false, value);
};

export const insertRichEditorText = (
  editor: HTMLElement | null,
  text: string,
) => {
  focusRichEditor(editor);
  document.execCommand("insertText", false, text);
};

export const insertRichEditorLink = (editor: HTMLElement | null) => {
  focusRichEditor(editor);
  const selection = window.getSelection();
  const selectedText = selection?.toString().trim() || "link";
  const url = window.prompt("Link URL", "https://");
  if (!url?.trim()) return;

  if (!selection?.toString()) {
    document.execCommand(
      "insertHTML",
      false,
      `<a href="${escapeHtml(url.trim())}">${escapeHtml(selectedText)}</a>`,
    );
    return;
  }

  document.execCommand("createLink", false, url.trim());
};

export const sanitizeComposerHtml = (html: string) => {
  if (typeof document === "undefined") return html;

  const container = inertDocument().createElement("div");
  // Remove cid: imgs as text first — innerHTML would otherwise trigger fetches.
  container.innerHTML = stripUnresolvedInlineImageTags(html);

  container
    .querySelectorAll("script,style,iframe,object,embed")
    .forEach((node) => {
      node.remove();
    });

  container.querySelectorAll("img").forEach((node) => {
    const src = node.getAttribute("src")?.trim().toLowerCase() ?? "";
    if (src.startsWith("cid:")) {
      node.remove();
    }
  });

  container.querySelectorAll("*").forEach((node) => {
    for (const attribute of Array.from(node.attributes)) {
      if (attribute.name.toLowerCase().startsWith("on")) {
        node.removeAttribute(attribute.name);
      }
    }
  });

  return container.innerHTML;
};

const PASTE_KEPT_ATTRIBUTES: Record<string, string[]> = {
  a: ["href"],
  img: ["src", "alt"],
};

/**
 * Pasted HTML keeps its structure (bold, italic, links, lists, paragraphs)
 * but drops colors, backgrounds, fonts and classes — e.g. text copied from a
 * tinted thread card or another email — so replies always send clean.
 */
export const cleanPastedHtml = (html: string) => {
  if (typeof DOMParser === "undefined") return html;
  // DOMParser builds an inert document: nothing loads or runs while cleaning.
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("script,style,meta,link,title,iframe,object,embed")
    .forEach((node) => node.remove());

  for (const node of Array.from(doc.body.querySelectorAll("*"))) {
    const tag = node.tagName.toLowerCase();
    const kept = PASTE_KEPT_ATTRIBUTES[tag] ?? [];
    for (const attribute of Array.from(node.attributes)) {
      if (!kept.includes(attribute.name.toLowerCase())) {
        node.removeAttribute(attribute.name);
      }
    }
    // Styling-only wrappers carry nothing once their attributes are gone.
    if (tag === "span" || tag === "font") {
      node.replaceWith(...Array.from(node.childNodes));
    }
  }

  return doc.body.innerHTML.trim();
};
